import { ICD10CM_SYSTEM } from "@/lib/diagnosisApi";
import { catalogItemToDiagnosis } from "@/lib/encounterDiagnoses";
import {
  listCommonDiagnoses,
  listProblemList,
  replaceCommonDiagnoses,
  replaceProblemList,
} from "@/lib/problemsApi";

/** Demo physician COMMON list. Seeded once per user when PostgreSQL has no prefs. */
export const DEMO_COMMON_DIAGNOSES = [
  { code: "M54.50", description: "Low back pain, unspecified" },
  { code: "M54.16", description: "Radiculopathy, lumbar region" },
  { code: "M54.2", description: "Cervicalgia" },
  { code: "G89.29", description: "Other chronic pain" },
  { code: "I10", description: "Essential (primary) hypertension" },
  { code: "E11.9", description: "Type 2 diabetes mellitus without complications" },
];

export function commonDiagnosesStorageKey(userId) {
  return `emrbymd.v3.user.commonDiagnoses.${userId || "anonymous"}`;
}

export function problemListStorageKey(patientId) {
  return `emrbymd.v3.patient.problems.${patientId || "unknown"}`;
}

function compactSourceItem(item) {
  const diagnosis = catalogItemToDiagnosis(item);
  if (!diagnosis.code) return null;
  return {
    code: diagnosis.code,
    description: diagnosis.description,
    system: diagnosis.system || ICD10CM_SYSTEM,
  };
}

function compactProblemItem(item) {
  const base = compactSourceItem(item);
  if (!base) return null;
  return {
    ...base,
    isPrimary: item?.isPrimary === true,
  };
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

export function readCommonDiagnoses(userId) {
  const key = commonDiagnosesStorageKey(userId);
  const parsed = readJson(key, null);
  if (parsed?.initialized === true && Array.isArray(parsed.items)) {
    return parsed.items.map(compactSourceItem).filter(Boolean);
  }
  const seeded = DEMO_COMMON_DIAGNOSES.map(compactSourceItem).filter(Boolean);
  localStorage.setItem(key, JSON.stringify({ initialized: true, items: seeded }));
  return seeded;
}

export function writeCommonDiagnoses(userId, items) {
  localStorage.setItem(
    commonDiagnosesStorageKey(userId),
    JSON.stringify({
      initialized: true,
      items: (items || []).map(compactSourceItem).filter(Boolean),
    }),
  );
}

export function readProblemList(patientId) {
  if (!patientId) return [];
  const parsed = readJson(problemListStorageKey(patientId), null);
  if (parsed?.initialized === true && Array.isArray(parsed.items)) {
    return parsed.items.map(compactProblemItem).filter(Boolean);
  }
  return [];
}

export function writeProblemList(patientId, items) {
  if (!patientId) return;
  localStorage.setItem(
    problemListStorageKey(patientId),
    JSON.stringify({
      initialized: true,
      items: (items || []).map(compactProblemItem).filter(Boolean),
    }),
  );
}

/**
 * Longitudinal Problem List is independent of today's Encounter Diagnoses.
 * Seed once from prior encounter codes; Lookup adds do not write here.
 */
export function ensureProblemList(patientId, seedItems = []) {
  if (!patientId) return [];
  const parsed = readJson(problemListStorageKey(patientId), null);
  if (parsed?.initialized === true) {
    return Array.isArray(parsed.items)
      ? parsed.items.map(compactProblemItem).filter(Boolean)
      : [];
  }
  const seeded = (seedItems || []).map(compactSourceItem).filter(Boolean);
  writeProblemList(patientId, seeded);
  return seeded;
}

/**
 * Explicit PL+ from Medical History. Does not write Encounter or Note diagnoses.
 */
export function promotePmhToProblemList(patientId, item) {
  return addProblemListItem(patientId, item);
}

export function addProblemListItem(patientId, item) {
  if (!patientId) return [];
  const current = readProblemList(patientId);
  const next = compactProblemItem(item);
  if (!next) return current;
  if (current.some((row) => row.code === next.code)) return current;
  const updated = [...current, { ...next, isPrimary: current.length === 0 }];
  writeProblemList(patientId, updated);
  return updated;
}

export function removeProblemListItem(patientId, code) {
  if (!patientId) return [];
  const updated = readProblemList(patientId).filter((row) => row.code !== code);
  if (updated.length && !updated.some((row) => row.isPrimary)) {
    updated[0] = { ...updated[0], isPrimary: true };
  }
  writeProblemList(patientId, updated);
  return updated;
}

export function moveProblemListItem(patientId, code, direction) {
  const list = [...readProblemList(patientId)];
  const index = list.findIndex((row) => row.code === code);
  const nextIndex = index + Number(direction);
  if (index < 0 || nextIndex < 0 || nextIndex >= list.length) return list;
  [list[index], list[nextIndex]] = [list[nextIndex], list[index]];
  writeProblemList(patientId, list);
  return list;
}

export function setPrimaryProblemListItem(patientId, code) {
  const updated = readProblemList(patientId).map((row) => ({
    ...row,
    isPrimary: row.code === code,
  }));
  writeProblemList(patientId, updated);
  return updated;
}

export function problemListWritesEncounterOrNote() {
  return false;
}

/**
 * Load Problem List from PostgreSQL. If the server has never been initialized
 * and this browser still has localStorage items, copy them to PostgreSQL once.
 * localStorage remains a cache and is not deleted.
 */
export async function hydrateProblemList(patientId, seedItems = []) {
  if (!patientId) return [];
  const local = readProblemList(patientId);
  try {
    const server = await listProblemList(patientId);
    if (server.initialized) {
      writeProblemList(patientId, server.items);
      return server.items.map(compactProblemItem).filter(Boolean);
    }
    const seeded = local.length
      ? local
      : (seedItems || []).map(compactProblemItem).filter(Boolean);
    const saved = await replaceProblemList(patientId, seeded);
    writeProblemList(patientId, saved);
    return saved.map(compactProblemItem).filter(Boolean);
  } catch {
    return ensureProblemList(patientId, seedItems);
  }
}

export async function persistProblemList(patientId, items) {
  const compacted = (items || []).map(compactProblemItem).filter(Boolean);
  writeProblemList(patientId, compacted);
  const saved = await replaceProblemList(patientId, compacted);
  writeProblemList(patientId, saved);
  return saved.map(compactProblemItem).filter(Boolean);
}

export async function addProblemListItemRemote(patientId, item) {
  return persistProblemList(patientId, addProblemListItem(patientId, item));
}

export async function removeProblemListItemRemote(patientId, code) {
  return persistProblemList(patientId, removeProblemListItem(patientId, code));
}

export async function moveProblemListItemRemote(patientId, code, direction) {
  return persistProblemList(patientId, moveProblemListItem(patientId, code, direction));
}

export async function setPrimaryProblemListItemRemote(patientId, code) {
  return persistProblemList(patientId, setPrimaryProblemListItem(patientId, code));
}

export async function promotePmhToProblemListRemote(patientId, item) {
  return addProblemListItemRemote(patientId, item);
}

/**
 * Load COMMON diagnoses from user preferences. Seed the demo list once when
 * PostgreSQL has no initialized preference document.
 */
export async function hydrateCommonDiagnoses(userId) {
  const local = readCommonDiagnoses(userId);
  try {
    const server = await listCommonDiagnoses();
    if (server.initialized) {
      writeCommonDiagnoses(userId, server.items);
      return server.items.map(compactSourceItem).filter(Boolean);
    }
    const saved = await replaceCommonDiagnoses(local);
    writeCommonDiagnoses(userId, saved);
    return saved.map(compactSourceItem).filter(Boolean);
  } catch {
    return local;
  }
}
